const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";

const SCRIPT_ID = "bytelogic-recaptcha-v3";

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

let loadPromise: Promise<void> | null = null;

const loadRecaptchaScript = (): Promise<void> => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("reCAPTCHA is only available in the browser"));
  }

  if (!SITE_KEY) {
    return Promise.reject(new Error("reCAPTCHA site key is missing"));
  }

  if (window.grecaptcha?.execute) {
    return Promise.resolve();
  }

  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load reCAPTCHA"))
      );
      if (window.grecaptcha?.execute) resolve();
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load reCAPTCHA"));
    document.head.appendChild(script);
  });

  return loadPromise;
};

/**
 * Execute reCAPTCHA v3 and return a token for the given action.
 */
export const getRecaptchaToken = async (action: string): Promise<string> => {
  // Local Next.js: skip Google script (keys often aren't registered for localhost).
  if (process.env.NODE_ENV === "development") {
    return "dev-bypass";
  }

  if (!SITE_KEY) {
    throw new Error("reCAPTCHA is not configured");
  }

  await loadRecaptchaScript();

  return new Promise<string>((resolve, reject) => {
    if (!window.grecaptcha) {
      reject(new Error("reCAPTCHA failed to initialize"));
      return;
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha!
        .execute(SITE_KEY, { action })
        .then((token) => {
          if (!token) {
            reject(new Error("Empty reCAPTCHA token"));
            return;
          }
          resolve(token);
        })
        .catch((err) => {
          reject(err instanceof Error ? err : new Error("reCAPTCHA execute failed"));
        });
    });
  });
};

export const RECAPTCHA_ACTIONS = {
  contactUs: "contact_us",
  getQuote: "get_quote",
  jobApplication: "job_application",
  subscribe: "subscribe",
} as const;
